"use client";

import { getOrCreateHomeSessionId } from "@/utils/homeSession";
import { getOrCreateTrialSessionId } from "@/utils/trialTracking";
import { getTrialBaseUrl } from "@/utils/trialUrl";

const SNAPSHOT_KEY = "sr_home_quickstart_snapshot_v1";

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
type TrialEventData = Record<string, unknown>;

type QuickstartSnapshot = {
  position?: string;
  level?: string;
  stage?: string;
  competencies?: string[];
  required?: string[];
  domains?: string[];
  excludes?: string[];
  events?: Array<Record<string, JsonValue>>;
  updatedAt?: number;
};

function readSnapshot(): QuickstartSnapshot {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(SNAPSHOT_KEY);
    return raw ? (JSON.parse(raw) as QuickstartSnapshot) : {};
  } catch {
    return {};
  }
}

function writeSnapshot(snapshot: QuickstartSnapshot) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore storage quota/private mode failures
  }
}

function asStringArray(value: unknown) {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : undefined;
}

function asString(value: unknown) {
  return typeof value === "string" && value ? value : undefined;
}

function compactData(data: TrialEventData): Record<string, JsonValue> {
  const out: Record<string, JsonValue> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || typeof value === "function") continue;
    if (Array.isArray(value)) {
      out[key] = value.filter(
        (item): item is JsonValue => item === null || ["string", "number", "boolean"].includes(typeof item),
      );
      continue;
    }
    if (value && typeof value === "object") {
      try {
        out[key] = JSON.parse(JSON.stringify(value)) as JsonValue;
      } catch {
        // skip non-serializable objects
      }
      continue;
    }
    if (value === null || ["string", "number", "boolean"].includes(typeof value)) {
      out[key] = value as JsonValue;
    }
  }
  return out;
}

function mergeSnapshot(type: string, data: TrialEventData) {
  const prev = readSnapshot();
  const event = compactData({
    type,
    ts: Date.now(),
    step: data.step ?? data.current_step ?? data.to_step,
    position: data.position,
    level: data.level,
    action: data.direction ?? data.method ?? data.source,
    target: data.competency ?? data.domain ?? data.exclude ?? data.stage,
  });

  const next: QuickstartSnapshot = {
    ...prev,
    position: asString(data.position) ?? prev.position,
    level: asString(data.level) ?? prev.level,
    stage: asString(data.stage) ?? prev.stage,
    competencies: asStringArray(data.competencies) ?? prev.competencies,
    required: asStringArray(data.required) ?? prev.required,
    domains: asStringArray(data.domains) ?? prev.domains,
    excludes: asStringArray(data.excludes) ?? prev.excludes,
    updatedAt: Date.now(),
    events: [...(prev.events || []), event].slice(-80),
  };
  writeSnapshot(next);
  return next;
}

export function trackTrialFunnelEvent(type: string, data: TrialEventData = {}) {
  if (typeof window === "undefined") return Promise.resolve();

  const trialSessionId = getOrCreateTrialSessionId();
  const homeSessionId = getOrCreateHomeSessionId();
  const quickstartSnapshot = mergeSnapshot(type, data);
  const body = JSON.stringify({
    sessionId: trialSessionId,
    type,
    data: {
      ...compactData(data),
      home_session_id: homeSessionId,
      quickstart_snapshot: quickstartSnapshot,
    },
  });

  return fetch(`${getTrialBaseUrl()}/api/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: body.length < 60_000,
    cache: "no-store",
  })
    .then(() => undefined)
    .catch(() => undefined);
}
